// src/features/calendar/WeeklyPeriodsEditor.tsx
// Weekly grid of time ranges — used by working hours and booking link availability.

import { useI18n } from '@react/hooks';
import { Button } from '@react/components';
import type { WorkingPeriod } from '@core/types/calendar';
import { WEEKDAYS, WEEKDAY_KEYS, periodsToWeek, weekToPeriods, DEFAULT_WORK_RANGE } from './time';

interface WeeklyPeriodsEditorProps {
  periods: WorkingPeriod[];
  onChange: (periods: WorkingPeriod[]) => void;
}

export function WeeklyPeriodsEditor({ periods, onChange }: WeeklyPeriodsEditorProps) {
  const { t } = useI18n();
  const week = periodsToWeek(periods);

  const update = (dayIdx: number, mut: (ranges: { start: string; end: string }[]) => { start: string; end: string }[]) => {
    const next = week.map((d, i) => (i === dayIdx ? { ...d, ranges: mut(d.ranges) } : d));
    onChange(weekToPeriods(next));
  };

  return (
    <div className="space-y-2">
      {week.map((d, dayIdx) => (
        <div key={d.day} className="flex flex-wrap items-start gap-2 rounded-md border border-border p-2">
          <span className="w-16 shrink-0 pt-1.5 text-sm font-medium text-foreground">
            {t(`calendar.weekday.${WEEKDAY_KEYS[WEEKDAYS[dayIdx]]}`)}
          </span>
          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            {d.ranges.length === 0 && (
              <span className="pt-1.5 text-sm text-muted-foreground">{t('calendar.workingHours.unavailable')}</span>
            )}
            {d.ranges.map((r, ri) => (
              <div key={ri} className="flex items-center gap-1.5">
                <input
                  type="time"
                  aria-label={t('calendar.workingHours.from')}
                  value={r.start}
                  onChange={(e) => update(dayIdx, (rs) => rs.map((x, i) => (i === ri ? { ...x, start: e.target.value } : x)))}
                  className="rounded-md border border-input bg-background px-2 py-1 text-sm text-foreground"
                />
                <span className="text-muted-foreground">–</span>
                <input
                  type="time"
                  aria-label={t('calendar.workingHours.to')}
                  value={r.end}
                  onChange={(e) => update(dayIdx, (rs) => rs.map((x, i) => (i === ri ? { ...x, end: e.target.value } : x)))}
                  className="rounded-md border border-input bg-background px-2 py-1 text-sm text-foreground"
                />
                <button
                  type="button"
                  aria-label={t('calendar.workingHours.removeRange')}
                  onClick={() => update(dayIdx, (rs) => rs.filter((_, i) => i !== ri))}
                  className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-destructive"
                >
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                    <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => update(dayIdx, (rs) => [...rs, { ...DEFAULT_WORK_RANGE }])}
          >
            {t('calendar.workingHours.addRange')}
          </Button>
        </div>
      ))}
    </div>
  );
}
